import { Badge, Box, Grid, Group, Loader, Text } from '@mantine/core'
import { useListState } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { IconGripVertical, IconSortDescending2 } from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import React from 'react'

import { Screen } from '@/components/Screen'
import { useGlobalState } from '@/hooks/useGlobalState'
import { useTopicsEnabled } from '@/hooks/useTopicsEnabled'
import type { GetContentModulesResponse } from '@/services/api/api.types'
import { ContentModuleStatus } from '@/services/api/api.types'
import * as commonClasses from '@/styles/componentCommons.css'
import { isHelpModule } from '@/utils/contentModuleTables'
import { applyReorderedPriorities, priorityForIndex, sortModulesByPriority } from '@/utils/modulePriority'

const statusColors: Record<string, string> = {
  published: 'green',
  draft: 'orange',
  removed: 'gray',
  pending_approval: 'blue',
  testing: 'yellow',
  complete: 'blue',
}

const getStatusLabel = (status: string) => {
  if (status === 'complete' || status === 'pending_approval') return 'PENDING'
  return status.toUpperCase()
}

export function PriorityScreen() {
  const { api } = useGlobalState()
  const queryClient = useQueryClient()
  const { topicsEnabled } = useTopicsEnabled()

  // Fetch and sort modules by priority
  const modulesQuery = useQuery({
    queryKey: ['cm', 'list'],
    queryFn: () => api.getContentModules(),
    select: data => {
      const filtered =
        data?.filter(module => module.status !== ContentModuleStatus.Removed && !isHelpModule(module)) ?? []
      return sortModulesByPriority(filtered)
    },
  })

  // Local state for drag and drop reordering
  const [modules, handlers] = useListState(modulesQuery.data ?? [])

  // Ref to always have the latest local state for drag handlers. Layout, not passive: a passive
  // effect is still queued when a drop lands in the same task as the commit that rendered the rows,
  // and the handler then reorders a list shorter than the one on screen (SME-1141).
  const modulesRef = React.useRef(modules)
  React.useLayoutEffect(() => {
    modulesRef.current = modules
  }, [modules])

  // Counter for in-flight mutations (boolean was insufficient for overlapping mutations)
  const inflightMutationsRef = React.useRef(0)
  // Monotonic ID so only the latest mutation writes to cache
  const mutationIdRef = React.useRef(0)

  // Keep local state in sync with server data (only when no mutations are in-flight)
  React.useEffect(() => {
    if (modulesQuery.data && inflightMutationsRef.current === 0) {
      handlers.setState(modulesQuery.data)
    }
  }, [modulesQuery.data])

  // Mutation to update priorities on the server
  const updatePriorityMutation = useMutation({
    mutationFn: (modules: GetContentModulesResponse) => {
      inflightMutationsRef.current++
      const myId = ++mutationIdRef.current
      return Promise.all(
        modules.map((module, index, array) => {
          if (!module?.taskId) return Promise.resolve()
          const priority = priorityForIndex(index, array.length)
          return api.updateContentModule({
            task_id: module.taskId,
            priority,
            // Bulk reorder happens outside an edit-lock session, so bypass the
            // backend lock check. Otherwise reordering fails for any module a
            // teammate currently has open in the editor.
            lock: false,
          })
        }),
      ).then(results => ({ results, myId })) as any
    },
    onSuccess: (payload: { myId: number }, variables) => {
      inflightMutationsRef.current = Math.max(0, inflightMutationsRef.current - 1)
      if (payload.myId !== mutationIdRef.current) return
      // A poll that started before these updates landed is still carrying pre-drag priorities, and
      // a resolving fetch commits unconditionally. Cancelling marks it synchronously so it cannot
      // land on top of the patch below; the request itself still completes, since api.request does
      // not plumb AbortSignal through to axios.
      queryClient.cancelQueries({ queryKey: ['cm', 'list'] })
      queryClient.setQueryData<GetContentModulesResponse>(['cm', 'list'], old =>
        applyReorderedPriorities(old, variables),
      )
      queryClient.invalidateQueries({
        queryKey: ['cm', 'list'],
        refetchType: 'none',
      })
    },
    onError: () => {
      inflightMutationsRef.current = Math.max(0, inflightMutationsRef.current - 1)
      if (inflightMutationsRef.current !== 0) return

      // A failed reorder wrote nothing, so the refetch below comes back byte-identical and
      // structural sharing hands back the same reference — the effect that syncs server order
      // into the dragged list never fires. Put the list back explicitly, or the screen keeps
      // showing an order that was never saved.
      if (modulesQuery.data) handlers.setState(modulesQuery.data)
      queryClient.invalidateQueries({ queryKey: ['cm', 'list'] })

      notifications.show({
        title: 'Error',
        message: 'Failed to reorder modules. Please try again.',
        color: 'red',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
    },
  })

  // Loading and error states
  if (modulesQuery.isError) {
    console.error('Error loading modules:', modulesQuery.error)
    return (
      <Screen title="Module Prioritization" icon={IconSortDescending2}>
        <Text c="red">Error loading modules</Text>
      </Screen>
    )
  }

  if (modulesQuery.isLoading) {
    return (
      <Screen title="Module Prioritization" icon={IconSortDescending2}>
        <Group justify="center" p="xl">
          <Loader />
        </Group>
      </Screen>
    )
  }

  if (!modules.length) {
    return (
      <Screen
        title="Module Prioritization"
        description="Drag your modules to rank them from highest priority to lowest"
        icon={IconSortDescending2}>
        <Text c="dimmed" ta="center" p="xl">
          No active modules found
        </Text>
      </Screen>
    )
  }

  // Render draggable module list
  const items = modules.map((item, index) => (
    <Grid key={item.taskId ?? index} align="center" gutter={0} data-priority-row>
      <Grid.Col span={0.6}>
        {/* Number label that highlights on drag over */}
        <Text
          size="lg"
          fw={500}
          c="dimmed"
          ta="left"
          px="sm"
          ref={(el: HTMLParagraphElement | null) => {
            if (el) el.dataset.index = index.toString()
          }}>
          {index + 1}
        </Text>
      </Grid.Col>
      <Grid.Col span={8}>
        <Group
          p="md"
          mb="xs"
          bg="white"
          w="100%"
          style={{
            borderRadius: 8,
            border: '1px solid #eee',
            cursor: 'grab',
          }}
          draggable
          onDragStart={e => {
            e.currentTarget.style.opacity = '0.5'
            e.dataTransfer?.setData('text/plain', item.taskId ?? '')
          }}
          onDragEnd={e => {
            e.currentTarget.style.opacity = '1'
            // Reset any highlighted numbers
            document.querySelectorAll(`[data-index="${index}"]`).forEach(el => {
              if (el instanceof HTMLElement) el.style.color = ''
            })
          }}
          onDragOver={e => {
            e.preventDefault()
            // Highlight the number when dragging over
            const numberEl = e.currentTarget.closest('[data-priority-row]')?.querySelector(`[data-index="${index}"]`)
            if (numberEl instanceof HTMLElement) numberEl.style.color = 'var(--mantine-color-blue-filled)'
          }}
          onDragLeave={e => {
            // Reset number highlight
            const numberEl = e.currentTarget.closest('[data-priority-row]')?.querySelector(`[data-index="${index}"]`)
            if (numberEl instanceof HTMLElement) numberEl.style.color = ''
          }}
          onDrop={e => {
            e.preventDefault()
            // Reset number highlight
            const numberEl = e.currentTarget.closest('[data-priority-row]')?.querySelector(`[data-index="${index}"]`)
            if (numberEl instanceof HTMLElement) numberEl.style.color = ''

            // Identify the dragged module by id, not by the position it held at dragstart. The
            // Content Library tab this screen lives in polls ['cm','list'] every 7s, and that
            // refetch resyncs the list mid-drag: a stale position moves whichever module now sits
            // there, and one past the end splices undefined in and crashes the render (SME-1141).
            const current = modulesRef.current
            const draggedTaskId = e.dataTransfer?.getData('text/plain')
            const fromIndex = current.findIndex(module => module.taskId === draggedTaskId)
            const toIndex = index
            // Dropping a module on its own row is a no-op that would still save every priority.
            if (fromIndex === -1 || fromIndex === toIndex) return

            const newModules = [...current]
            const [movedItem] = newModules.splice(fromIndex, 1)
            newModules.splice(toIndex, 0, movedItem)

            // Update local state and ref immediately
            handlers.setState(newModules)
            modulesRef.current = newModules

            // Send update to server
            updatePriorityMutation.mutate(newModules)
          }}>
          <IconGripVertical size={18} style={{ cursor: 'grab' }} />
          <Group justify="space-between" style={{ flex: 1 }}>
            <Group gap="sm">
              <Box>{item.name ?? 'Unnamed Module'}</Box>
              {item.status && (
                <Badge variant="filled" color={statusColors[item.status] ?? 'gray'} size="sm">
                  {getStatusLabel(item.status)}
                </Badge>
              )}
            </Group>
            {topicsEnabled && item.categories?.[0] && (
              <Text c="dimmed" size="sm">
                Topic: {item.categories[0]}
              </Text>
            )}
          </Group>
        </Group>
      </Grid.Col>
    </Grid>
  ))

  return (
    <Screen
      title="Module Prioritization"
      description={<Text ta="left">Drag the modules to rank them from highest priority to lowest</Text>}
      icon={IconSortDescending2}>
      <Box>{items}</Box>
    </Screen>
  )
}
